import { useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  Landmark,
  Shield,
  Truck,
  type LucideIcon,
} from "lucide-react";
import LeadCaptureModal from "../components/LeadCaptureModal";
import type { LeadRequestType } from "../services/lead-capture-api";

const PILOT_STEPS: { title: string; description: string; Icon: LucideIcon }[] = [
  {
    title: "Discovery",
    description:
      "Map shipment types, document ownership, review steps, and current compliance bottlenecks.",
    Icon: Truck,
  },
  {
    title: "Evidence Review",
    description:
      "Review one representative shipment workflow for completeness, consistency, and missing evidence.",
    Icon: FileSearch,
  },
  {
    title: "Risk Workflow",
    description:
      "Demonstrate VAT, registry, exception, case, and audit workflow handling where configured.",
    Icon: Landmark,
  },
  {
    title: "Findings Report",
    description:
      "Summarize evidence gaps, manual review friction, risk categories, and rollout recommendations.",
    Icon: ClipboardCheck,
  },
];

const SUCCESS_CRITERIA = [
  "Faster document readiness review",
  "Clearer exception ownership",
  "Better visibility into shipment risk",
  "Reduced manual reconciliation across teams",
  "More audit-ready evidence history",
  "Stronger coordination between logistics, finance, and compliance",
];

export default function PilotOfferPage() {
  const [modalRequestType, setModalRequestType] =
    useState<LeadRequestType | null>(null);

  return (
    <main className="min-h-screen bg-[#050814] text-white">
      <LeadCaptureModal
        open={modalRequestType !== null}
        onClose={() => setModalRequestType(null)}
        requestType={modalRequestType ?? "demo"}
        title={
          modalRequestType === "pilot_review"
            ? "Request a Pilot Review"
            : "Book a Compliance Readiness Demo"
        }
        description="Tell us a bit about your team and we'll get back to you shortly."
      />

      <section className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-8 sm:px-6 lg:px-8">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_22%_8%,rgba(56,189,248,0.18),transparent_30%),radial-gradient(circle_at_82%_20%,rgba(16,185,129,0.12),transparent_24%),linear-gradient(135deg,rgba(15,23,42,0.96),rgba(2,6,23,1))]" />

        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5">
          <a href="/" className="text-lg font-bold text-white">
            LightLink AI
          </a>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setModalRequestType("demo")}
              className="rounded-lg border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-slate-100 transition-colors hover:border-sky-300/50 hover:bg-sky-300/10"
            >
              Book a Compliance Readiness Demo
            </button>
            <button
              type="button"
              onClick={() => setModalRequestType("pilot_review")}
              className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-400"
            >
              Request a Pilot Review
            </button>
          </div>
        </header>

        <div className="grid flex-1 items-center gap-8 py-10 lg:grid-cols-[1.02fr_0.98fr]">
          <section className="space-y-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/25 bg-emerald-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-200">
              <Shield size={14} />
              Pilot offer
            </div>

            <div className="space-y-5">
              <h1 className="max-w-4xl text-4xl font-bold leading-tight text-white sm:text-5xl">
                30-Day Compliance Readiness Pilot
              </h1>
              <p className="max-w-3xl text-xl font-semibold leading-8 text-slate-200">
                Review one representative shipment workflow and find where
                document gaps, registry risks, manual bottlenecks, and audit
                exposure are hiding.
              </p>
              <p className="max-w-3xl text-sm leading-7 text-slate-400 sm:text-base">
                Built for UK and European logistics companies, freight teams,
                customs-linked operations, and manufacturers with internal
                logistics workflows.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setModalRequestType("pilot_review")}
                className="inline-flex items-center justify-center rounded-lg bg-sky-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-950/30 transition-colors hover:bg-sky-400"
              >
                Request a Pilot Review
              </button>
              <button
                type="button"
                onClick={() => setModalRequestType("demo")}
                className="inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-slate-100 transition-colors hover:border-sky-300/50 hover:bg-sky-300/10"
              >
                Book a Compliance Readiness Demo
              </button>
            </div>

            <section className="rounded-2xl border border-white/10 bg-white/[0.055] p-5">
              <h2 className="text-lg font-semibold text-white">
                Pilot Success Criteria
              </h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {SUCCESS_CRITERIA.map((item) => (
                  <div key={item} className="flex gap-3 text-sm text-slate-300">
                    <CheckCircle2
                      className="mt-0.5 shrink-0 text-emerald-300"
                      size={16}
                    />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          </section>

          <section className="rounded-2xl border border-sky-300/25 bg-slate-950/80 p-5 shadow-2xl shadow-sky-950/30">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-sky-200">
                Pilot workflow
              </p>
              <h2 className="mt-2 text-2xl font-bold text-white">
                From sample shipment to findings report
              </h2>
            </div>

            <div className="mt-5 grid gap-3">
              {PILOT_STEPS.map(({ title, description, Icon }, index) => (
                <article
                  key={title}
                  className="rounded-xl border border-white/10 bg-white/[0.045] p-4"
                >
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-400/15 text-sky-200">
                      <Icon size={19} />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-slate-300">
                          Step {index + 1}
                        </span>
                        <h3 className="text-sm font-semibold text-white">
                          {title}
                        </h3>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-400">
                        {description}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm leading-6 text-emerald-50">
              The pilot is an operational readiness assessment, not a generic
              software trial. The outcome is a practical findings report your
              team can use to decide whether to roll out LightLink AI.
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
